import config from '@/config';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
} from '@tanstack/react-router';
import React from 'react';
import { createPortal } from 'react-dom';
import { type AuthContextData } from 'src/context/auth.context';
import NotFoundPage from './NotFoundPage';
import { QueryClient } from '@tanstack/react-query';
import type { ModuleEnabledFunction } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { CopyrightOverlay } from '@/components/common/CopyrightOverlay';
import type { AuthzClient } from '@/context/authz/AuthzClient';
import { cn } from '@/lib/utils';
import { useLocalStorageValue } from '@react-hookz/web';
import { z } from 'zod';

const LoaderCrumbSchema = z.object({
  crumb: z.string().optional(),
});

if (config.isDev) {
  // @ts-expect-error - just a devtools thing, only affects development
  window.toggleDevtools = function toggleDevtools() {
    const hideDevtools =
      localStorage.getItem('GENTLY_HIDE_DEVTOOLS') === 'true';
    localStorage.setItem(
      'GENTLY_HIDE_DEVTOOLS',
      hideDevtools ? 'false' : 'true',
    );
    window.location.reload();
  };
}

export interface RouterContext {
  auth: AuthContextData;
  queryClient: QueryClient;
  isModuleEnabled: ModuleEnabledFunction;
  authzClient: AuthzClient;
}

const TanStackRouterDevtools = config.isDev
  ? React.lazy(() =>
      import('@tanstack/react-router-devtools').then((res) => ({
        default: res.TanStackRouterDevtools,
      })),
    )
  : (): null => null;

function RootRoute() {
  const { value: hideDevtools } = useLocalStorageValue<boolean>(
    'GENTLY_HIDE_DEVTOOLS',
    {
      defaultValue: true,
    },
  );

  return (
    <>
      {createPortal(<HeadContent />, document.head)}
      <Outlet />
      <CopyrightOverlay />
      {!hideDevtools && (
        <TanStackRouterDevtools
          position="bottom-right"
          initialIsOpen={false}
          toggleButtonProps={{
            className: cn('opacity-50 hover:opacity-100'),
          }}
        />
      )}
    </>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootRoute,
  notFoundComponent: () => <NotFoundPage />,
  head: (ctx) => {
    // Find the deepest route with a crumb in loaderData
    const lastMatchWithCrumb = [...ctx.matches].reverse().find((m) => {
      const result = LoaderCrumbSchema.safeParse(m.loaderData);
      return result.success && result.data.crumb;
    });

    const parsedLoaderData = LoaderCrumbSchema.safeParse(
      lastMatchWithCrumb?.loaderData,
    );
    const pageTitle = parsedLoaderData.success
      ? parsedLoaderData.data.crumb
      : undefined;

    return {
      meta: [
        {
          title: pageTitle
            ? `${config.appName} - ${pageTitle}`
            : config.appName,
        },
      ],
    };
  },
});
